import { motion } from "framer-motion";

const MILESTONES = [
    {
        year: "2026",
        quarter: "Q1",
        pillar: "Regulation",
        title: "CBAM transitional reporting live",
        detail: "EU importers must report embedded emissions on cement, steel, aluminium, fertiliser, hydrogen, and electricity. Default price: €85 / tCO₂e.",
        tone: "gold"
    },
    {
        year: "2026",
        quarter: "Q2",
        pillar: "Regulation",
        title: "SEBI BRSR Core · assurance mandate",
        detail: "Top-1000 listed Indian entities must obtain independent reasonable assurance on BRSR Core disclosures — a first for the Indian market.",
        tone: "navy"
    },
    {
        year: "2026",
        quarter: "Q3",
        pillar: "Regulation",
        title: "RBI Draft Framework consultation closes",
        detail: "Reserve Bank finalises its Disclosure Framework on Climate-Related Financial Risks. Board-level climate strategy becomes a supervisory expectation.",
        tone: "navy"
    },
    {
        year: "2027",
        quarter: "Q1",
        pillar: "Procurement",
        title: "CBAM verified dossier window opens",
        detail: "Exporters shift from default emissions values to verified product-level dossiers. First large-scale delistings by EU OEMs begin.",
        tone: "gold"
    },
    {
        year: "2027",
        quarter: "Q2",
        pillar: "Capital",
        title: "Financed emissions become supervisor-tracked",
        detail: "PCAF v2-aligned financed-emission attribution enters Indian bank ICAAP. First 40-90 bps COC uplifts modelled on high-intensity portfolios.",
        tone: "navy"
    },
    {
        year: "2028",
        quarter: "Q1",
        pillar: "Regulation",
        title: "CBAM full pricing regime",
        detail: "Transitional period ends. Every covered import into the EU is priced at the verified emissions cost — no exemptions, no phasing.",
        tone: "gold"
    }
];

export default function Timeline() {
    return (
        <section
            className="relative py-24 lg:py-32 border-t border-navy/10 bg-navy/[0.02] overflow-hidden"
            data-testid="section-timeline"
        >
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
                                When Things Land
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-navy">
                            The next 24 months, <em className="text-gold not-italic font-light">on a fixed calendar.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            Six enforceable dates between now and 2028. Each one reprices a piece of the balance sheet — and none of them are optional.
                        </p>
                    </div>
                </div>

                {/* Horizontal ribbon (desktop) */}
                <div className="hidden md:block relative">
                    {/* Base track */}
                    <div className="absolute left-0 right-0 top-[42px] h-px bg-navy/15"></div>

                    <div className="relative grid grid-cols-6 gap-4">
                        {MILESTONES.map((m, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.7, delay: i * 0.09 }}
                                className="relative flex flex-col items-start"
                                data-testid={`milestone-${m.year}-${m.quarter}`}
                            >
                                {/* Year + Quarter label */}
                                <div className="flex items-baseline gap-2 mb-4">
                                    <span className="font-serif text-2xl text-navy tracking-tight">{m.year}</span>
                                    <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-navy/50">{m.quarter}</span>
                                </div>

                                {/* Node */}
                                <div className="relative">
                                    <div className={`w-4 h-4 rounded-full border-2 ${m.tone === "gold" ? "bg-gold border-gold" : "bg-paper border-navy/60"}`}></div>
                                    {m.tone === "gold" && (
                                        <div className="absolute inset-0 w-4 h-4 rounded-full bg-gold/30 animate-ping"></div>
                                    )}
                                </div>

                                {/* Card */}
                                <div className="mt-6 border-l border-navy/15 pl-4 pr-2">
                                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold mb-2">
                                        {m.pillar}
                                    </div>
                                    <div className="font-serif text-[17px] leading-[1.25] text-navy mb-3">
                                        {m.title}
                                    </div>
                                    <p className="text-navy/55 text-[12.5px] leading-relaxed">
                                        {m.detail}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Vertical list (mobile) */}
                <div className="md:hidden flex flex-col gap-8 relative before:content-[''] before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-navy/15">
                    {MILESTONES.map((m, i) => (
                        <div key={i} className="relative pl-8" data-testid={`milestone-m-${m.year}-${m.quarter}`}>
                            <div className={`absolute left-0 top-1 w-4 h-4 rounded-full border-2 ${m.tone === "gold" ? "bg-gold border-gold" : "bg-paper border-navy/60"}`}></div>
                            <div className="flex items-baseline gap-2 mb-1">
                                <span className="font-serif text-xl text-navy">{m.year}</span>
                                <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-navy/50">{m.quarter}</span>
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold mb-2">
                                {m.pillar}
                            </div>
                            <div className="font-serif text-lg leading-snug text-navy mb-2">
                                {m.title}
                            </div>
                            <p className="text-navy/55 text-[13px] leading-relaxed">{m.detail}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
